/**
 * <tessi-why-switch> — the hover-driven "Why Tessi" pillars on the Our Story page.
 * Added 2026-07-29, from a Rhode reference she sent: a hairline list of pillar NAMES at the
 * bottom of the panel, and hovering one swaps the big title + paragraph at the top.
 *
 * WHY THIS IS A CUSTOM ELEMENT, not an inline <script>:
 *   CLAUDE.md's number-one recurring trap is that inline scripts DO NOT EXECUTE when Shopify
 *   re-renders a section in the theme editor. A custom element is upgraded in any markup the
 *   browser parses, re-renders included, so the swap comes back on its own after she changes a
 *   setting. assets/tessi-hero-video.js and assets/video-background.js use the same pattern.
 *
 * WHY IT IS NOT PURE CSS:
 *   This was first built with `:has()` and no JavaScript, which was appealing precisely because
 *   it could not be broken by the re-render trap. It was replaced because the state it depended
 *   on could not be verified: `:hover` needs a real pointer and `:focus` only applies while the
 *   DOCUMENT itself has focus, so neither can be exercised from a script in a headless or
 *   unfocused window. A class toggled by this element is directly assertable, which means the
 *   behaviour can actually be tested instead of assumed.
 *
 * CONTRACT WITH sections/tessi-our-story.liquid:
 *   .tessi-why__item     one per pillar, all stacked in a single CSS grid cell
 *   .tessi-why__trigger  one <button> per pillar, in the same source order
 *   Exactly one item carries `.is-active` at a time, and the matching trigger carries it too.
 *   `data-ready` lands on the host once wired, which is what retires the CSS-only fallback
 *   that shows the first pillar when JavaScript never runs.
 *   ⚠️ Items and triggers are paired BY INDEX. Keep the two loops in the same order.
 */

class TessiWhySwitch extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.tessi-why__item'));
    this.triggers = Array.from(this.querySelectorAll('.tessi-why__trigger'));
    if (!this.items.length || !this.triggers.length) return;

    // Bound once so the same reference can be removed again on disconnect.
    this.onEnter = (event) => {
      const i = this.triggers.indexOf(event.currentTarget);
      if (i !== -1) this.activate(i);
    };

    this.triggers.forEach((trigger) => {
      // `mouseenter` rather than `mouseover`: it does not re-fire for descendants.
      trigger.addEventListener('mouseenter', this.onEnter);
      // Keyboard users get the same swap by tabbing, and a TAP on a touch device focuses the
      // button, so this doubles as the touch path without needing a separate handler.
      trigger.addEventListener('focus', this.onEnter);
      // Belt and braces for touch browsers that focus late or not at all.
      trigger.addEventListener('click', this.onEnter);
    });

    // ⚠️ Set the opening state from JS as well as CSS. `data-ready` retires the no-JS
    // fallback in the same breath, so there is never a frame with two pillars visible.
    this.activate(0);
    this.dataset.ready = '1';
  }

  /** Reveals pillar `index` and marks its trigger. Cheap and idempotent. */
  activate(index) {
    if (index === this.current) return;
    this.current = index;

    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });
    this.triggers.forEach((trigger, i) => {
      const on = i === index;
      trigger.classList.toggle('is-active', on);
      // Announces which pillar is showing, for anyone driving this by keyboard.
      trigger.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  disconnectedCallback() {
    if (!this.triggers) return;
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('mouseenter', this.onEnter);
      trigger.removeEventListener('focus', this.onEnter);
      trigger.removeEventListener('click', this.onEnter);
    });
  }
}

if (!customElements.get('tessi-why-switch')) {
  customElements.define('tessi-why-switch', TessiWhySwitch);
}
